import { ArrowLeft, MapPin, HardHat, Pencil, Plus, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { STATUS_LABEL, STATUS_TONE, SLA_TONE, shortId, fmtMoney } from "./util";
import ContractorBadgeForName from "@/components/admin/ContractorBadgeForName";

/* WorkspaceHeader — refreshed for visual authority.
   - H1 is the customer name and *dominates* the screen.
   - Identity strip below is one tight line: park, plot, town, contractor + badge.
   - Status / Priority / SLA pills sit beside the H1, heavier than before.
   - Header actions stay top-right but slimmer to leave room for the title.
*/

export default function WorkspaceHeader({ ws, onReload, onBack }) {
  const c = ws.case;
  const status = (c.status || "NEW").toUpperCase();
  const tone = STATUS_TONE[status] || STATUS_TONE.NEW;
  const sla = SLA_TONE[ws.sla.status] || SLA_TONE.GREEN;
  const ct = (c.case_type || "GENERAL").replace(/_/g, " ");
  const backTo = ct === "QUOTE REVIEW" ? "/adminpanel/quote-reviews" : "/adminpanel/support-cases";

  const slaText = ws.sla.completed
    ? "On track"
    : ws.sla.hours_remaining < 0
      ? `${Math.abs(ws.sla.hours_remaining).toFixed(1)} h over`
      : `${ws.sla.hours_remaining.toFixed(1)} h left`;

  return (
    <header className="bg-[#071A33] text-white border-b border-black/30" data-testid="workspace-header">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-white/45 mb-4">
          <Link to={backTo} className="hover:text-white">{ct}</Link>
          <ChevronRight size={11}/>
          <span className="text-white/70">Workspace</span>
          <ChevronRight size={11}/>
          <span className="font-mono tracking-[0.14em] text-white/80" data-testid="workspace-job-id">{shortId(c.id)}</span>
        </div>

        <div className="flex items-start gap-6 flex-wrap">
          {/* Identity block — H1 dominates */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-x-3 gap-y-2 flex-wrap">
              <h1 className="font-display text-[34px] lg:text-[44px] xl:text-[52px] leading-[1.02] tracking-tight text-white" data-testid="workspace-customer-name">
                {c.full_name || "(Unknown customer)"}
              </h1>
              {/* Pills sit at the baseline of H1 */}
              <div className="flex items-center gap-2 flex-wrap pb-1">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] tracking-[0.08em] uppercase font-semibold border ${tone}`} data-testid="workspace-status-badge">
                  {STATUS_LABEL[status] || status}
                </span>
                {c.high_value && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] tracking-[0.08em] uppercase font-semibold bg-[#B88A2A] text-white shadow-sm">
                    Priority
                  </span>
                )}
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] tracking-[0.08em] uppercase font-semibold ${sla.bg} ${sla.text}`} data-testid="workspace-sla-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/90"/> SLA · {slaText}
                </span>
              </div>
            </div>

            {/* Identity strip — one tight, scannable line. Falls onto two on phones. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[13px] text-white/75">
              {c.park_name && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#B88A2A]"/>
                  <span className="text-white/90 font-medium">{c.park_name}</span>
                  {c.plot_number && <span className="text-white/55">· Plot {c.plot_number}</span>}
                </span>
              )}
              {c.town_location && <span className="text-white/55">{c.town_location}</span>}
              {c.contractor_name && (
                <span className="flex items-center gap-1.5">
                  <HardHat size={14} className="text-[#0F766E]"/>
                  <span className="text-white/90 font-medium">{c.contractor_name}</span>
                  <span className="inline-block"><ContractorBadgeForName name={c.contractor_name} size="sm"/></span>
                </span>
              )}
              {c.quote_value > 0 && (
                <span className="flex items-center gap-1.5 ml-auto sm:ml-0">
                  <span className="text-white/45 text-[10px] uppercase tracking-[0.16em]">Quote</span>
                  <span className="text-white font-display text-base tabular-nums">{fmtMoney(c.quote_value)}</span>
                </span>
              )}
            </div>
          </div>

          {/* Header actions */}
          <div className="flex items-center gap-2 self-start" data-testid="workspace-actions">
            <button onClick={onBack} className="text-[12px] text-white/65 hover:text-white inline-flex items-center gap-1.5 px-3 py-2 rounded-md hover:bg-white/[0.08] transition" data-testid="workspace-back-btn">
              <ArrowLeft size={13}/> Back
            </button>
            <button onClick={() => document.querySelector('[data-testid="ws-add-note-btn"]')?.click()} className="text-[12px] bg-white/[0.10] hover:bg-white/20 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md transition font-medium" data-testid="workspace-add-note-header">
              <Plus size={13}/> Add note
            </button>
            <button onClick={() => document.querySelector('[data-testid="ws-edit-job-btn"]')?.click()} className="text-[12px] bg-white text-[#071A33] hover:bg-[#FFE7B6] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md font-semibold transition shadow-sm" data-testid="workspace-edit-job-btn">
              <Pencil size={13}/> Edit job
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
